import React, { useState, useEffect, useLayoutEffect } from 'react';
import uniqid from 'uniqid';

import { Stack } from 'ui/stack';
import { Box } from 'ui/box';

import { Loader } from 'ui/loader';
import Breadcrumbs from 'components/partials/beadcrumbs/breadcrumbs';
import BasicInfo from 'containers/user/learn/components/basic-info/basic-info';
import RelatedContent from 'containers/user/learn/components/related-content/related-content';
import PageNav from 'containers/user/learn/components/page-nav/page-nav';
import GetStartedGuideBanner from 'containers/user/learn/components/get-started-guide-banner/get-started-guide-banner';

import api from 'api';

import { useCurrentUser } from 'hooks/api/use-current-user';

import { getBlocksFromContent } from 'containers/user/learn/heplers/get-blocks-from-content';
import {
  getIsRelatedContentShown,
  getRelatedContent,
} from 'containers/user/learn/heplers/content-builder-helpers';
import { onResize } from 'helpers/resize';
import ResponsiveWrapper from 'components/shared/others/responsive-wrapper/responsive-wrapper';
import { Typography } from 'ui/typography';

interface ArticleData {
  name?: string;
  content?: Array<{
    id: string | number;
    type: string;
    [key: string]: unknown;
  }>;
  authorName?: string;
  authorPosition?: string;
  authorAvatar?: { data?: Array<{ attributes?: { url?: string } }> };
  readTime?: number;
  updatedAt?: string;
}

interface ArticleProps {
  articleId: string | number;
}

const Article = ({ articleId }: ArticleProps): React.JSX.Element => {
  const [article, setArticle] = useState<ArticleData>({});
  const [loading, setLoading] = useState<boolean>(false);
  const [isTabletView, setIsTabletView] = useState<boolean>(false);

  const user = useCurrentUser();

  useEffect(() => {
    setLoading(true);
    api
      .getArticle(articleId, user?.data?.oktaUserId ?? '')
      .then((res) => {
        setArticle(
          (res as { data: { data: { attributes: ArticleData } } }).data.data
            .attributes
        );
      })
      .catch(console.error)
      .finally(() => setLoading(false));
  }, []);

  useLayoutEffect(() => {
    onResize(setIsTabletView);
  }, []);

  return loading ? (
    <Loader {...{ loading }} />
  ) : (
    <ResponsiveWrapper customClass="flex flex-col">
      <Breadcrumbs
        breadcrumbs={[
          { label: 'Learn', href: '/learn' },
          { label: 'Articles', href: '/learn/content/articles' },
        ]}
        customClass="!mt-5"
      />
      <Typography
        data-testid="article-heading"
        variant="h1"
        sx={{ pt: { mobile: 2, xs: 3 } }}
      >
        {article.name}
      </Typography>
      <BasicInfo {...article} />
      {isTabletView && (
        <PageNav content={article.content} isTabletView={true} />
      )}
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          order: 1,
          '@media (min-width: 375px)': { flexDirection: 'column' },
          '@media (min-width: 1280px)': { flexDirection: 'row' },
        }}
      >
        <ResponsiveWrapper customClass="flex flex-col">
          {article.content?.map((component) =>
            getBlocksFromContent({ ...component, key: uniqid() })
          )}
          {/* <WasResourseHelpfulBanner relatedTags={article.relatedTags} /> */}
          {getIsRelatedContentShown(article as never, 'articleIsLinkedTo') && (
            <RelatedContent
              content={
                getRelatedContent(
                  article as never,
                  'articleIsLinkedTo'
                ) as never
              }
            />
          )}
        </ResponsiveWrapper>
        <Stack>
          {!isTabletView && <PageNav content={article.content} />}
          <GetStartedGuideBanner
            isPopoverBanner={false}
            customClass="md:max-w-[300px] p-6 mobile:mb-6 xs:mb-8 mobile:flex-col tablet:flex-row md:flex-col tablet:gap-6 md:gap-1"
            textFontSize="text-base"
            headerFontSize="text-xl"
          />
        </Stack>
      </Box>
    </ResponsiveWrapper>
  );
};

export default Article;
